import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, AlertTitle, Avatar, Box, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import {
  ChatBubbleOutline as CommentIcon,
  Place as PlaceIcon,
  VisibilityOff as HiddenIcon,
} from '@mui/icons-material';
import { visuallyHidden } from '@mui/utils';
import {
  POST_STATUS,
  authorInitials,
  formatRelativeDate,
  moderationReason,
  postTypeSx,
} from '../../constants/community';
import ModeratorBadge from './ModeratorBadge';
import ModerateDialog from './ModerateDialog';
import PhotoGallery from './PhotoGallery';
import { useOptionalAuth } from '../../context/AuthContext';

/**
 * One entry in the feed.
 *
 * The type chip carries the colour, because the type is what makes the feed
 * scannable: a lost cat and a recommendation are not the same errand.
 *
 * A row that is not published is only ever shown to its author or to a
 * moderator — the API decides that, not this component. It says so plainly:
 * a post silently missing from the feed is the failure this whole flow has to
 * avoid, and a grey chip on its own is not an explanation.
 */
function PostCard({ post, onModerated }) {
  const { user } = useOptionalAuth();
  const status = POST_STATUS[post.status];
  const isLive = 'PUBLISHED' === post.status;
  const reason = moderationReason(post);

  return (
    <Card>
      <CardContent sx={{ p: { xs: 2.5, sm: 3.25 }, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {!isLive && status?.notice && (
          <Alert severity="warning" icon={<HiddenIcon />}>
            <AlertTitle>{status.label}</AlertTitle>
            {status.notice}
            {reason && (
              <Box component="span" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                Motif&nbsp;: {reason}
              </Box>
            )}
          </Alert>
        )}

        <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Chip label={post.type_label} size="small" sx={postTypeSx(post.type)} />
          {post.neighborhood && (
            <Chip icon={<PlaceIcon />} label={post.neighborhood.name} size="small" />
          )}
        </Stack>

        <Typography
          component={RouterLink}
          to={`/quartier/${post.uuid}`}
          variant="h4"
          sx={{
            display: 'block',
            color: 'text.primary',
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          {post.title}
        </Typography>

        <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
          {post.body}
        </Typography>

        <PhotoGallery medias={post.medias} title={post.title} compact />

        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            pt: 1.75,
            mt: 0.25,
            borderTop: '1px solid',
            borderColor: 'surface.rule',
          }}
        >
          <Stack direction="row" sx={{ alignItems: 'center', gap: 1.25, minWidth: 0 }}>
            <Avatar
              aria-hidden="true"
              sx={{ width: 38, height: 38, fontSize: '0.90625rem', bgcolor: 'success.light', color: 'secondary.dark' }}
            >
              {authorInitials(post.author)}
            </Avatar>
            <Typography variant="caption" noWrap>
              <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {post.author}
              </Box>
              <ModeratorBadge show={post.author_is_moderator} />
              {' · '}
              {formatRelativeDate(post.created_at)}
              {post.updated_at ? ' · modifié' : ''}
            </Typography>
          </Stack>

          <Stack
            direction="row"
            sx={{ alignItems: 'center', gap: 0.75, flexShrink: 0, color: 'text.secondary' }}
          >
            <ModerateDialog
              targetType="POST"
              targetUuid={post.uuid}
              citySlug={post.city_slug}
              isOwn={Boolean(user?.username) && user.username === post.author_username}
              hidden={!isLive}
              onModerated={onModerated}
            />
            <CommentIcon sx={{ fontSize: 18 }} />
            <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
              {post.comment_count}
            </Typography>
            {/* MUI's own hidden-text style rather than a hand-rolled one: in
                `sx`, a bare `width: 1` means 100%, not one pixel, so the
                version this replaces was an absolutely positioned box as tall
                as the page — it stretched the document and left a gap under
                the footer. */}
            <Box component="span" sx={visuallyHidden}>
              {1 === post.comment_count ? 'réponse' : 'réponses'}
            </Box>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default PostCard;
